/**
 * Shared types matching the DB enums and the app_identity() RPC return shape.
 *
 * Day 3+ extends with location types. Future days will replace hand-written
 * types with `supabase gen types` output.
 */

export type AppRole = 'system_admin' | 'owner' | 'manager' | 'staff';

export type LocationStatus = 'draft' | 'active' | 'suspended' | 'closed';

export interface IdentityUser {
  id: string;
  email: string | null;
  display_name: string;
  preferred_contact: string | null;
  avatar_url: string | null;
}

export interface IdentityOrganization {
  id: string;
  legal_name: string;
  display_name: string;
  timezone: string;
  currency: string;
  status: 'active' | 'frozen';
  support_access_enabled: boolean;
}

export interface IdentityFlags {
  is_platform_admin: boolean;
  mfa_required: boolean;
  mfa_enrolled: boolean;
  is_sole_owner: boolean;
}

export interface Identity {
  user: IdentityUser;
  role: AppRole;
  organization: IdentityOrganization;
  flags: IdentityFlags;
  /** Day 15.x — 0086: org subscription summary (tier + status). */
  license?: { tier: string; status: string };
  /** Future: support_session object (Day 8) */
  support_session?: {
    session_id: string;
    target_org_id: string;
    reason: string;
    ends_at: string;
  };
  /** Day 15.x — 0086: server-evaluated entitlements. has_pay_rates gates the labor-cost feature. */
  feature_flags?: { has_pay_rates?: boolean } & Record<string, boolean>;
}

// =============================================================================
// Location domain
// =============================================================================

export type CalendarDensity = 'standard' | 'dense' | 'spacious';
export type TimeFormat = 'am_pm' | 'h24';

export interface Location {
  id: string;
  organization_id: string;
  name: string;
  address: string | null;
  phone: string | null;
  /** IANA timezone, or null to inherit from organization. */
  timezone: string | null;
  /** TIME format 'HH:MM:SS' or 'HH:MM'. Default '12:00:00'. */
  shift_split_time: string;
  status: LocationStatus;
  opened_at: string | null;
  closed_at: string | null;
  /** Day 6.5: per-pharmacy tag dropdown source for the calendar cell. */
  day_annotation_tags: string[];
  /** Day 6.5: per-pharmacy calendar density preset. */
  calendar_density: CalendarDensity;
  /** Day 6.5: per-pharmacy time format preference. */
  time_format: TimeFormat;
  /** Day 15.x — 0082: owner-set rule. Schedule for the next month must be published at least this many days before the 1st. Default 10. */
  schedule_publish_lead_days: number;
  created_at: string;
  updated_at: string;
}

/** Postgres EXTRACT(dow): 0=Sunday, 1=Monday, ..., 6=Saturday. */
export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const DAYS_OF_WEEK: { value: DayOfWeek; label: string; short: string }[] = [
  { value: 0, label: 'Sunday', short: 'Sun' },
  { value: 1, label: 'Monday', short: 'Mon' },
  { value: 2, label: 'Tuesday', short: 'Tue' },
  { value: 3, label: 'Wednesday', short: 'Wed' },
  { value: 4, label: 'Thursday', short: 'Thu' },
  { value: 5, label: 'Friday', short: 'Fri' },
  { value: 6, label: 'Saturday', short: 'Sat' },
];

export interface LocationHours {
  location_id: string;
  day_of_week: DayOfWeek;
  open_time: string | null;
  close_time: string | null;
  is_closed: boolean;
  created_at: string;
  updated_at: string;
}

export interface LocationHourOverride {
  location_id: string;
  override_date: string; // ISO date YYYY-MM-DD
  open_time: string | null;
  close_time: string | null;
  is_closed: boolean;
  reason: string;
  created_at: string;
  updated_at: string;
}

export interface LocationManager {
  id: string;
  location_id: string;
  user_id: string;
  assigned_at: string;
  assigned_by: string | null;
}

// =============================================================================
// People (Day 4)
// =============================================================================

export type JobType =
  | 'pharmacist'
  | 'pharmacy_technician'
  | 'pharmacy_assistant'
  | 'student'
  | 'manager'
  | 'other';

export const JOB_TYPES: { value: JobType; label: string }[] = [
  { value: 'pharmacist', label: 'Pharmacist' },
  { value: 'pharmacy_technician', label: 'Pharmacy technician' },
  { value: 'pharmacy_assistant', label: 'Pharmacy assistant' },
  { value: 'student', label: 'Student / Intern' },
  { value: 'manager', label: 'Manager' },
  { value: 'other', label: 'Other' },
];

/**
 * Job types a user may PICK when creating/editing a staff record.
 *
 * 'manager' is deliberately excluded: manager is a *role* granted via the
 * "Promote to manager" flow (staff_promote_to_manager), NOT a job type you
 * choose. Picking it from a dropdown used to write staff.job_type='manager'
 * with no validation -- it showed a "Manager" label but granted zero
 * privileges, and let non-pharmacists / multiple people look like managers.
 * The enum value is kept in JOB_TYPES for label lookups of legacy rows; a
 * DB trigger (migration 0099) blocks any NEW write that sets it.
 */
export const SELECTABLE_JOB_TYPES: { value: JobType; label: string }[] =
  JOB_TYPES.filter((j) => j.value !== 'manager');

export type EmploymentType = 'full_time' | 'part_time' | 'casual' | 'relief' | 'contract';

export const EMPLOYMENT_TYPES: { value: EmploymentType; label: string }[] = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
  { value: 'casual', label: 'Casual' },
  { value: 'relief', label: 'Relief' },
  { value: 'contract', label: 'Contract' },
];

/** Period over which staff.min_hours / max_hours apply (0081). */
export type HoursTargetPeriod = 'week' | 'month';

export const HOURS_TARGET_PERIODS: { value: HoursTargetPeriod; label: string }[] = [
  { value: 'week', label: 'Per week' },
  { value: 'month', label: 'Per month' },
];

export interface Staff {
  id: string;
  organization_id: string;
  profile_id: string | null;
  display_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  job_type: JobType;
  employment_type: EmploymentType;
  primary_location_id: string | null;
  active_from: string; // ISO date YYYY-MM-DD
  active_to: string | null;
  /** Day 15.x — 0081: target floor for scheduled hours (per period). 0 = no floor. */
  min_hours: number;
  /** Day 15.x — 0081: target ceiling for scheduled hours (per period). Default 40. */
  max_hours: number;
  /** Day 15.x — 0081: 'week' or 'month'. Reports normalize across cadences. */
  hours_target_period: HoursTargetPeriod;
  /** Day 15.x — 0084: hourly pay rate in CAD. Pro/Enterprise tier only. NULL = not set. */
  hourly_rate: number | null;
  /**
   * 0109: owner/manager-supplied display initials for calendar chip + sidebar
   * avatar. Overrides the derived-from-name default. 1-4 chars, uppercase
   * [A-Z.-]. NULL = fall back to derived.
   */
  custom_initials: string | null;
  /** 0097: 32-byte base64url token used in the personal .ics subscription URL. */
  calendar_feed_token: string;
  created_at: string;
  updated_at: string;
}

export interface StaffLocationAssignment {
  staff_id: string;
  location_id: string;
  role_note: string | null;
  assigned_at: string;
  assigned_by: string | null;
}

// =============================================================================
// Invites (Day 4)
// =============================================================================

export type InviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';
/** The role the invite grants on acceptance. Owners are not invited. */
export type InviteAppRole = 'manager' | 'staff';

export interface StaffInvite {
  id: string;
  organization_id: string;
  staff_id: string;
  email: string;
  app_role: InviteAppRole;
  location_ids: string[];
  status: InviteStatus;
  expires_at: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  consumed_by_profile_id: string | null;
  consumed_at: string | null;
  last_failure_reason: string | null;
}

/** Returned by invite_create / invite_resend. The raw_token is shown ONCE. */
export interface InviteCreateResult {
  invite_id: string;
  raw_token: string;
  expires_at: string;
}

/** Returned by invite_preview. */
export interface InvitePreview {
  email: string | null;
  app_role: InviteAppRole | null;
  org_name: string | null;
  expires_at: string | null;
  is_valid: boolean;
  failure_reason: string | null;
  /**
   * Prefill for the invite-accept form. When the invite is linked to a staff
   * row, this is the staff.display_name as it stands today. The invitee can
   * edit it; if they do, invite_accept() will sync staff.display_name and
   * log the rename to the INVITE_ACCEPTED audit payload. Null for invites
   * without a staff link (rare, e.g. admin-only invites).
   */
  staff_display_name: string | null;
}

// =============================================================================
// Scheduling (Day 5)
// =============================================================================

export type ScheduleStatus = 'draft' | 'published' | 'closed';

export type ConflictCode =
  | 'DOUBLE_BOOK'
  | 'INACTIVE_STAFF'
  | 'TIME_OFF_OVERLAP'
  | 'OUT_OF_HOURS';

export interface Schedule {
  id: string;
  organization_id: string;
  location_id: string;
  /** YYYY-MM-DD; always day=1. */
  month_start_date: string;
  status: ScheduleStatus;
  notes: string | null;
  published_at: string | null;
  published_by: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface Shift {
  id: string;
  organization_id: string;
  location_id: string;
  schedule_id: string;
  shift_date: string;
  /** HH:MM:SS */
  start_time: string;
  /** HH:MM:SS */
  end_time: string;
  break_minutes: number;
  staff_id: string | null;
  is_open: boolean;
  staff_display_name_at_save: string | null;
  /**
   * 0109: snapshot of the staff's effective display initials at write time
   * (custom OR derived). NULL for shifts created before 0109 or for open
   * shifts. Client falls back to derived-from-name in that case.
   */
  staff_initials_at_save: string | null;
  notes: string | null;
  override_reason: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface DayAnnotation {
  id: string;
  organization_id: string;
  location_id: string;
  annotation_date: string;
  note: string | null;
  /** Day 6.5: tags chosen from location.day_annotation_tags. */
  tags: string[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

// =============================================================================
// Time-off (Day 7)
// =============================================================================

export type TimeOffStatus =
  | 'pending'
  | 'approved'
  | 'denied'
  | 'withdrawn'
  | 'cancelled';

export type TimeOffType =
  | 'vacation'
  | 'sick'
  | 'personal'
  | 'unpaid'
  | 'bereavement'
  | 'other';

export const TIME_OFF_TYPES: { value: TimeOffType; label: string }[] = [
  { value: 'vacation', label: 'Vacation' },
  { value: 'sick', label: 'Sick' },
  { value: 'personal', label: 'Personal' },
  { value: 'unpaid', label: 'Unpaid leave' },
  { value: 'bereavement', label: 'Bereavement' },
  { value: 'other', label: 'Other' },
];

export type HalfDayPart = 'AM' | 'PM';

export interface TimeOffRequest {
  id: string;
  organization_id: string;
  staff_id: string;
  type: TimeOffType;
  /** YYYY-MM-DD */
  start_date: string;
  /** YYYY-MM-DD */
  end_date: string;
  starts_half_day: HalfDayPart | null;
  ends_half_day: HalfDayPart | null;
  reason: string | null;
  status: TimeOffStatus;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  cancelled_by: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TimeOffConflictShift {
  shift_id: string;
  schedule_id: string;
  shift_date: string;
  start_time: string;
  end_time: string;
  location_id: string;
  location_name: string;
  status: ScheduleStatus;
}

// =============================================================================
// Admin / Audit (Day 8)
// =============================================================================

export interface PlatformSettings {
  id: string;
  maintenance_mode: boolean;
  maintenance_message: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export type AuditEventType = string;

export interface AuditEvent {
  event_id: string;
  organization_id: string | null;
  actor_user_id: string | null;
  actor_role_at_time: AppRole | null;
  event_type: AuditEventType;
  target_type: string | null;
  target_id: string | null;
  summary: string;
  payload: Record<string, unknown>;
  occurred_at: string;
  total_count?: number;
}

export interface AdminOrgSummary {
  organization_id: string;
  display_name: string;
  status: string;
  primary_contact_email: string;
  staff_count: number;
  location_count: number;
  shifts_30d: number;
  created_at: string;
}

// =============================================================================
// Communication Banners (Day 9 / FE-01)
// =============================================================================

export type BannerSeverity = 'info' | 'warning' | 'success' | 'promo';
export type BannerStatus = 'draft' | 'published' | 'archived';
export type BannerTargetKind = 'all' | 'specific_orgs' | 'specific_roles' | 'my_org_all' | 'my_org_roles';
export type BannerLayout = 'compact' | 'media_left' | 'media_bg';

export interface Banner {
  id: string;
  title: string;
  body_md: string;
  cta_text: string | null;
  cta_url: string | null;
  severity: BannerSeverity;
  status: BannerStatus;
  starts_at: string;
  ends_at: string | null;
  target_kind: BannerTargetKind;
  target_org_ids: string[];
  target_roles: AppRole[];
  target_tiers: string[];
  author_organization_id: string | null;
  acknowledgement_required: boolean;
  image_url: string | null;
  image_alt: string | null;
  layout: BannerLayout;
  custom_bg_hex: string | null;
  custom_text_hex: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/** Returned by banners_active_for_me() — lean shape for the banner bar. */

/** Returned by banners_active_for_me() — lean shape for the banner bar. */
export interface ActiveBanner {
  id: string;
  title: string;
  body_md: string;
  cta_text: string | null;
  cta_url: string | null;
  severity: BannerSeverity;
  acknowledgement_required: boolean;
  image_url: string | null;
  image_alt: string | null;
  layout: BannerLayout;
  custom_bg_hex: string | null;
  custom_text_hex: string | null;
}

// =============================================================================
// Subscriptions / billing (0074 + 0079)
// =============================================================================

export type SubscriptionStatus =
  | 'trial'
  | 'pilot'
  | 'active'
  | 'past_due'
  | 'canceled';

export type SubscriptionTier = 'standard' | 'pro' | 'enterprise';

export type BillingPeriod = 'monthly' | 'annual';

export interface OrgSubscription {
  organization_id: string;
  status: SubscriptionStatus;
  tier: SubscriptionTier | null;
  billing_period: BillingPeriod | null;
  trial_ends_at: string | null;
  pilot_ends_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  internal_note: string | null;
  // Discount fields (0079)
  applied_coupon_id: string | null;
  discount_pct: number | null;
  discount_amount_off_cad: number | null;
  discount_ends_at: string | null;
  discount_reason: string | null;
  created_at: string;
  updated_at: string;
}
